import type { Workspace } from '@rhythmic/db/types'
import type { TeamType } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'
import { watch } from 'vue'

import useCollectionRegistry from '../../../collections/CollectionRegistry'
import type { TeamContextEvents } from '../useTeamContext.events.schema'

type TeamContextHookMap = {
  [K in keyof TeamContextEvents]: (event: TeamContextEvents[K]) => void | Promise<void>
}

/**
 * Handle for removing a previously registered team context hook.
 */
type TeamContextUnsubscribe = () => void

interface TeamContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface UseTeamContextOnWorkspaceChangedParams {
  hooks: Hookable<TeamContextHookMap>
  logger: TeamContextLoggerLike
  workspaceQuery: Ref<UseLiveQueryReturn<Workspace> | undefined>
  teamQuery: Ref<UseLiveQueryReturn<TeamType> | undefined>
  clearWorkspaceScopedQueries: () => void
  createWorkspaceQuery: (workspaceSlug: string) => UseLiveQueryReturn<Workspace> | undefined
  createTeamsQuery: (workspaceSlug: string) => UseLiveQueryReturn<TeamType> | undefined
}

/**
 * Internal handler: updates workspace/team list queries when the active workspace changes.
 * Defers creating collection-based queries until the collection registry is initialized
 * so that early WorkspaceChanged events (e.g. from route sync) do not throw.
 */
export default function useTeamContextOnWorkspaceChanged({
  hooks,
  logger,
  workspaceQuery,
  teamQuery,
  clearWorkspaceScopedQueries,
  createWorkspaceQuery,
  createTeamsQuery,
}: UseTeamContextOnWorkspaceChangedParams) {
  let pendingRegistryWatchStop: (() => void) | null = null

  function applyQueriesForSlug(workspaceSlug: string) {
    workspaceQuery.value = createWorkspaceQuery(workspaceSlug)
    teamQuery.value = createTeamsQuery(workspaceSlug)
  }

  const unsubscribe: TeamContextUnsubscribe = hooks.hook(
    'WorkspaceChanged',
    ({ workspaceSlug, previousWorkspaceSlug, path }) => {
      logger.debug('Workspace changed, updating team context workspace queries', {
        path,
        previousWorkspaceSlug,
        workspaceSlug,
      })

      pendingRegistryWatchStop?.()
      pendingRegistryWatchStop = null
      clearWorkspaceScopedQueries()

      if (!workspaceSlug) {
        workspaceQuery.value = undefined
        teamQuery.value = undefined
        return
      }

      const registry = useCollectionRegistry()
      const initRef = (registry as unknown as { isInitialized?: { value: boolean } }).isInitialized
      const initialized = Boolean(initRef?.value)

      if (!initialized) {
        pendingRegistryWatchStop = watch(
          () => registry.isInitialized,
          (init) => {
            if (init) {
              pendingRegistryWatchStop?.()
              pendingRegistryWatchStop = null
              applyQueriesForSlug(workspaceSlug)
            }
          },
          { immediate: true },
        )
        return
      }

      applyQueriesForSlug(workspaceSlug)
    },
  )

  return unsubscribe
}
